import React, { useEffect, useMemo, useState } from 'react';
import styles from '../insertsPages.module.css';
import Tabs from '../../../Tabs';
import { FileInput } from '../../../../../../personalisation/src/components/FileInput';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Chooser } from '../../../Chooser';
import { Visualiser, VisualiserImageArea, VisualiserTextBox } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct } from '../../../../utilities/visualiserVariants.js';

const tabConfig = [
  { id: 'FRONT', label: 'Front' },
  { id: 'BACK', label: 'Back' }
];

/**
 * InsertThemedPhotoUpload component allows users to upload a photo for their themed box insert.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function InsertThemedPhotoUpload({ ...rest }) {
  const {
    getImageInput,
    setImageInput,
    getTextInput,
    setTextInput,
    getTransformation,
    setTransformation,
    setVisualiserSelection,
    clearVisualiserSelection
  } = useUserInput();

  const [activeIndex, setActiveIndex] = useState(0);

  const frontImageKey = 'insertThemedPhotoUpload.frontImage';
  const frontImage = getImageInput(frontImageKey);
  const frontTransformation = getTransformation(frontImageKey);

  const themedProduct = useMemo(() => {
    return getInsertsAndCardsProduct({ productKey: 'themedInserts' });
  }, []);

  const imageVariants = useMemo(() => {
    if (!themedProduct || !Array.isArray(themedProduct.variants)) return [];
    return themedProduct.variants.filter((variant) => variant?.option1 === 'Image Upload');
  }, [themedProduct]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    imageVariants && imageVariants.length > 0 ? imageVariants[0].id : null
  );

  useEffect(() => {
    if (!imageVariants || imageVariants.length === 0) return;
    if (!selectedVariantId || !imageVariants.find((variant) => variant.id === selectedVariantId)) {
      setSelectedVariantId(imageVariants[0].id);
    }
  }, [imageVariants, selectedVariantId]);

  const selectedVariant = useMemo(
    () => imageVariants.find((variant) => variant.id === selectedVariantId) || null,
    [imageVariants, selectedVariantId]
  );

  useEffect(() => {
    const productKey = 'themedInserts';
    setVisualiserSelection({
      productKey,
      variant: selectedVariant,
      config: {
        templateId: 'INSERT_THEMED_IMAGE_TEXT',
        mapping: {
          text: {
            'insertThemedPhotoUpload.front': 'UVFrontText',
            'insertThemedPhotoUpload.back': 'UVBackText'
          },
          images: {
            'insertThemedPhotoUpload.frontImage': 'UVFrontImage'
          }
        }
      }
    });
  }, [selectedVariant, setVisualiserSelection]);

  useEffect(() => {
    return () => {
      clearVisualiserSelection();
    };
  }, [clearVisualiserSelection]);

  const backgroundImageUrl = useMemo(() => {
    const raw = selectedVariant?.featured_image?.src || selectedVariant?.featured_media?.preview_image?.src || null;
    if (!raw || typeof raw !== 'string') return null;
    return raw.startsWith('/' + '/') ? `https:${raw}` : raw;
  }, [selectedVariant]);

  return (
    <div style={{ ...rest }} className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs tabs={tabConfig} activeIndex={activeIndex} onChange={setActiveIndex} fullWidth={true} />
        {activeIndex === 0 && (
          <Visualiser
            type="INSERT"
            background={backgroundImageUrl ? `url(${backgroundImageUrl})` : 'var(--color-white)'}
            subtitle="Upload an image below"
          >
            <VisualiserImageArea
              value={frontImage}
              size={{
                width: '163px',
                height: '169px'
              }}
              position={{
                top: '62px',
                left: '77px'
              }}
              borderRadius="0"
              backgroundColor="var(--color-white)"
              style={{ 
                border: '0.1rem solid rgba(0, 0, 0, 0.3)'
              }}
              alt="Themed box insert photo upload preview"
              scale={frontTransformation.scale}
              x={frontTransformation.x}
              y={frontTransformation.y}
              onUpdate={(updates) => setTransformation(frontImageKey, updates)}
            />
            <VisualiserTextBox
              size={{
                width: '180px',
                minWidth: '180px',
                maxWidth: '180px',
                minHeight: '20px',
                maxHeight: '20px'
              }}
              position={{
                top: '235px',
                left: '68px'
              }}
              fontWeight="bold"
              maxLength={40}
              maxLines={1}
              font="Romie"
              fontSize={24}
              color={'#751521'}
              style={{
                textAlign: 'center',
              }}
              value={getTextInput('insertThemedPhotoUpload.front')}
              setValue={((value) => setTextInput('insertThemedPhotoUpload.front', value))}
            />
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <Visualiser
            type="INSERT"
            background={`url(${window?.giftWrapData?.insertsAndCards?.images?.insertBack})`}
            subtitle="Tap to edit"
          >
            <VisualiserTextBox
              size={{
                width: '280px',
                minWidth: '200px',
                maxWidth: '280px',
                minHeight: '50px',
                maxHeight: '200px'
              }}
              position={{
                top: '50%',
                left: '50%'
              }}
              fontWeight="bold"
              maxLength={100}
              maxLines={8}
              lineHeight={1.2}
              fontSize={21}
              style={{
                transform: 'translate(-50%, -50%)'
              }}
              value={getTextInput('insertThemedPhotoUpload.back')}
              setValue={(value) => setTextInput('insertThemedPhotoUpload.back', value)}
            />
          </Visualiser>
        )}

        {activeIndex === 0 && (
          <div className={styles['file-input-container']}>
            <FileInput
              label="Upload a Photo"
              onFileSelect={(value) => setImageInput(frontImageKey, value)}
              accept={'png,jpg,jpeg,webp'}
              disabled={false}
              buttonVariant="primary"
              buttonColor="orange"
              buttonSize="small"
              maxFileSize={5 * 1024 * 1024}
              id="front-image"
              selectedFile={
                frontImage && frontImage.userImage && frontImage.userImage.file instanceof File
                  ? frontImage.userImage.file
                  : null
              }
            />
          </div>
        )}
      </div>


      {imageVariants.length > 0 && (
        <Chooser
          title="Choose a themed insert design"
          subtitle={
            selectedVariant
              ? `${selectedVariant.public_title || selectedVariant.title || 'Design selected'}`.replace('Image Upload / ', '')
              : 'Select a design'
          }
        >
          <div className={styles['chooser--content']}>
            <div className={styles['stock-card--chooser-grid']}>
              {imageVariants.map((variant) => {
                const variantImg =
                  (variant?.featured_image && variant.featured_image.src) ||
                  variant?.featured_media?.preview_image?.src ||
                  null;
                const variantImgUrl =
                  variantImg && typeof variantImg === 'string'
                    ? variantImg.startsWith('/' + '/')
                      ? `https:${variantImg}`
                      : variantImg
                    : null;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    className={`${styles['stock-card--chooser-item']} ${
                      selectedVariantId === variant.id ? styles['stock-card--chooser-item--selected'] : ''
                    }`}
                    onClick={() => setSelectedVariantId(variant.id)}
                  >
                    {variantImgUrl ? <img src={variantImgUrl} alt={variant.title || 'Insert design'} /> : null}
                    <p>{`${variant.public_title || variant.title || 'Insert design'}`.replace('Image Upload / ', '')}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </Chooser>
      )}
    </div>
  );
}

